/*
 * Contains the logic for a timer that can be started, paused, reset, and navigated through steps.
 */

import { useCallback, useReducer } from 'react';
import { COUNTDOWN } from '@/constants';
import track from '@/utils/track';
import useIntervalWithReset from './useIntervalWithReset';
import useSettings from './useSettings';
import useWakeLock from './useWakeLock';

export type TimerState = {
  step: number;
  active: boolean;
  remaining: number;
  isRunning: boolean;
  initial: boolean;
  done: boolean;
};

export type TimerStateWithDispatch = TimerState & {
  dispatch: (action: Action) => void;
};

export const initialState = {
  step: 0,
  initial: true,
  active: false,
  remaining: COUNTDOWN,
  isRunning: false,
  done: false,
};

export type Action
  = | 'START'
    | 'RESUME'
    | 'PAUSE'
    | 'RESET'
    | 'NEXT'
    | 'PREVIOUS'
    | 'TICK';

function useTimer(): TimerStateWithDispatch {
  const { duration, rest, repeats } = useSettings();
  const [resetTrigger, triggerReset] = useReducer(x => x + 1, 0);

  const [timerState, dispatch] = useReducer(reducer, initialState);
  useWakeLock(timerState.isRunning);

  useIntervalWithReset(
    () => dispatch('TICK'),
    timerState.isRunning ? 1000 : null,
    resetTrigger,
  );

  function nextState(state: TimerState): TimerState {
    if (repeats && state.step >= repeats) {
      return { ...state, done: true, isRunning: false };
    }
    return state.active
      ? { ...state, step: state.step, remaining: rest, active: false }
      : { ...state, step: state.step + 1, remaining: duration, active: true };
  }

  function reducer(state: TimerState, action: Action): TimerState {
    switch (action) {
      case 'START':
        if (!state.isRunning) {
          return { ...initialState, initial: false, isRunning: true };
        }
        return state;
      case 'RESUME':
        if (!state.isRunning) {
          return { ...state, isRunning: true };
        }
        return state;
      case 'PAUSE':
        if (state.isRunning) {
          return { ...state, isRunning: false };
        }
        return state;
      case 'RESET':
        return { ...initialState };
      case 'NEXT':
        triggerReset();
        return nextState(state);
      case 'PREVIOUS':
        triggerReset();
        if (state.step === 0) {
          return { ...state, remaining: COUNTDOWN };
        }
        return { ...state, remaining: state.active ? duration : rest };
      case 'TICK': {
        const newRemaining = state.remaining - 1;
        return newRemaining === 0 ? nextState(state) : { ...state, remaining: newRemaining };
      }
      default:
        throw new Error('Invalid action');
    };
  }

  const trackedDispatch = useCallback((action: Action) => {
    if (action === 'START') {
      track('start', { duration, rest, repeats });
    }
    dispatch(action);
  }, [dispatch, duration, rest, repeats]);
  return { ...timerState, dispatch: trackedDispatch };
}

export default useTimer;
